/** Bundle-relative path to a sprite asset (without `bundle://` prefix or `/spriteFrame` suffix). */
export type BrandableSpritePath = string;

/**
 * A card icon: a frame of a sprite sheet in the game's shared visuals bundle (`sheet` is the
 * bundle-relative sheet path, e.g. `intro/introIcons`). The chrome rasterises the frame for the DOM
 * splash when it opens.
 */
export interface IntroSplashCardIcon {
  sheet: string;
  frame: string;
}

/**
 * One intro card. Scene authoring is gone, so the per-card content
 * the prefab carried on its `Card1..3` nodes (icon binding + `LocalizedLabel` keys) lives here.
 */
export interface IntroSplashCardSpec {
  /** `null` = no icon (the cards bound an empty path and rendered iconless). */
  icon: IntroSplashCardIcon | null;
  titleKey: string;
  titleFallback: string;
  bodyKey: string;
  bodyFallback: string;
}

/**
 * Shape of the branded intro splash configuration. Per-game value lives at
 * `games/<game>/src/info/IntroSplashSpec.ts`; the presenter reads the dynamic pieces (volatility
 * pips, headline badge), the panel builder reads the cards.
 */
/** The splash's content when a title draws its own intro (decision 77): no cards, no pips, no badge. */
export const NO_INTRO_CARDS: IntroSplashSpec = { volatilityRating: 0, headlineBadgeAssetPath: null, cards: [] };

export interface IntroSplashSpec {
  /**
   * Volatility rating, 1..N where N = total pip count rendered. Presenter
   * activates the first `volatilityRating` pips and deactivates the rest.
   * Out-of-range values are clamped at apply time.
   */
  volatilityRating: number;

  /**
   * Optional marquee-feature sticker (top-right corner in the reference
   * design). Used purely as a show/hide gate — when set to any non-null
   * string, the badge slot is shown; when `null`, hidden. (The original badge art was
   * scene-authored; no game ships one yet, so the slot is empty.)
   */
  headlineBadgeAssetPath: BrandableSpritePath | null;

  /** The cards in display order (three in the reference design: desktop row / portrait carousel). */
  cards: readonly IntroSplashCardSpec[];
}
